import { useMemo } from "react";
import {
  GlobeAltIcon,
  NewspaperIcon,
  DocumentTextIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";

const SEVERITY_CONFIG = [
  {
    label: "High",
    value: "high",
    color: "#EF4444",
    bg: "rgba(239, 68, 68, 0.15)",
  },
  {
    label: "Medium",
    value: "medium",
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.15)",
  },
  {
    label: "Low",
    value: "low",
    color: "#10B981",
    bg: "rgba(16, 185, 129, 0.15)",
  },
];

const COUNTRY_FLAGS = {
  Algeria: "🇩🇿",
  Benin: "🇧🇯",
  USA: "🇺🇸",
  "United States": "🇺🇸",
  UK: "🇬🇧",
  "United Kingdom": "🇬🇧",
  France: "🇫🇷",
  Germany: "🇩🇪",
  Spain: "🇪🇸",
  Italy: "🇮🇹",
  Canada: "🇨🇦",
  China: "🇨🇳",
  Japan: "🇯🇵",
  India: "🇮🇳",
  Brazil: "🇧🇷",
  Mexico: "🇲🇽",
  Russia: "🇷🇺",
  Australia: "🇦🇺",
  Kenya: "🇰🇪",
  Angola: "🇦🇴",
};

export function StatisticsPanel({
  totalIncidents,
  filteredCount,
  allData,
  activeFilters,
  onFilterToggle,
}) {
  const hasActiveFilters =
    activeFilters?.severity?.length > 0 ||
    activeFilters?.sources?.length > 0 ||
    activeFilters?.countries?.length > 0;

  const relevanceCounts = useMemo(() => {
    return (
      allData?.reduce((acc, item) => {
        const severity = item.severity || "unavailable";
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      }, {}) || {}
    );
  }, [allData]);

  const topSources = useMemo(() => {
    const sourceCounts =
      allData?.reduce((acc, item) => {
        const source = item.source || "Unknown";
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {}) || {};

    return Object.entries(sourceCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([source, count]) => ({ source, count }));
  }, [allData]);

  const topCountries = useMemo(() => {
    const countryCounts =
      allData?.reduce((acc, item) => {
        const country = item.country || "Unknown";
        acc[country] = (acc[country] || 0) + 1;
        return acc;
      }, {}) || {};

    return Object.entries(countryCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([country, count]) => ({ country, count }));
  }, [allData]);

  const getSourceIcon = (source) => {
    const iconClass = "h-5 w-5 text-slate-400 dark:text-gray-400";
    if (source.includes("bbc")) return <NewspaperIcon className={iconClass} />;
    if (source.includes("aps")) return <SignalIcon className={iconClass} />;
    if (source.includes("algeria"))
      return <GlobeAltIcon className={iconClass} />;
    return <DocumentTextIcon className={iconClass} />;
  };

  const getCountryFlag = (country) => COUNTRY_FLAGS[country] || "🌍";

  const isSeverityActive = (value) =>
    activeFilters?.severity?.includes(value) || false;
  const isSourceActive = (source) =>
    activeFilters?.sources?.includes(source) || false;
  const isCountryActive = (country) =>
    activeFilters?.countries?.includes(country) || false;

  const panelClasses = [
    "rounded-lg border backdrop-blur-xl shadow-lg overflow-hidden",
    "bg-white/90 dark:bg-[#0B0F14]/90",
    "border-slate-200/70 dark:border-[#0EA5E9]/20",
  ].join(" ");

  return (
    <div className={panelClasses}>
      <div className="p-4 border-b border-slate-200 dark:border-[#0EA5E9]/20 bg-slate-50/50 dark:bg-gray-900/30">
        <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-widest mb-1">
          Total Incidents
        </div>
        <div className="text-slate-800 dark:text-gray-100 text-4xl font-light">
          {totalIncidents}
        </div>
        {hasActiveFilters && (
          <div className="text-[#0EA5E9] text-sm mt-1">
            {filteredCount} matching filters
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-widest mb-3">
            Relevance Breakdown
          </div>
          <div className="space-y-2">
            {SEVERITY_CONFIG.map((level) => {
              const count = relevanceCounts[level.value] || 0;
              const isActive = isSeverityActive(level.value);

              return (
                <button
                  key={level.label}
                  onClick={() => onFilterToggle("severity", level.value)}
                  className={[
                    "w-full p-2.5 rounded-lg transition-all duration-200",
                    isActive
                      ? "ring-2 ring-[#0EA5E9]/50 shadow-lg scale-[1.02] border-l-4"
                      : "border-l-2 hover:scale-[1.01]",
                  ].join(" ")}
                  style={{
                    background: level.bg,
                    borderLeftColor: level.color,
                    opacity: isActive ? 1 : 0.8,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${isActive ? "animate-pulse" : ""}`}
                        style={{ background: level.color }}
                      />
                      <span
                        className="text-xs uppercase tracking-wide"
                        style={{ color: level.color }}
                      >
                        {level.label}
                      </span>
                      {isActive && (
                        <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-[#0EA5E9]/20 text-[#0EA5E9]">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <span className="text-slate-800 dark:text-gray-100 text-lg font-light">
                      {count}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-[#0EA5E9]/20 pt-4">
          <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-widest mb-3">
            Top Sources
          </div>
          <div className="space-y-2">
            {topSources.map(({ source, count }) => {
              const isActive = isSourceActive(source);

              return (
                <button
                  key={source}
                  onClick={() => onFilterToggle("sources", source)}
                  className={[
                    "w-full flex items-center justify-between p-2.5 rounded-lg border transition-all duration-200",
                    "bg-slate-50 dark:bg-gray-900/40",
                    "hover:bg-slate-100 dark:hover:bg-gray-900/60",
                    isActive
                      ? "ring-2 ring-[#0EA5E9]/50 border-[#0EA5E9]/40 shadow-lg scale-[1.02]"
                      : "border-slate-200 dark:border-[#0EA5E9]/10",
                  ].join(" ")}
                  style={{ opacity: isActive ? 1 : 0.8 }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {getSourceIcon(source)}
                    <span className="text-slate-700 dark:text-gray-200 text-xs truncate">
                      {source}
                    </span>
                    {isActive && (
                      <span className="text-[#0EA5E9] text-[10px] px-1.5 py-0.5 bg-[#0EA5E9]/20 rounded shrink-0">
                        ✓
                      </span>
                    )}
                  </div>
                  <span className="text-[#0EA5E9] text-sm font-light ml-2">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-[#0EA5E9]/20 pt-4">
          <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-widest mb-3">
            Country Breakdown
          </div>
          <div className="space-y-2">
            {topCountries.map(({ country, count }) => {
              const isActive = isCountryActive(country);

              return (
                <button
                  key={country}
                  onClick={() => onFilterToggle("countries", country)}
                  className={[
                    "w-full flex items-center justify-between p-2.5 rounded-lg border transition-all duration-200",
                    "bg-slate-50 dark:bg-gray-900/40",
                    "hover:bg-slate-100 dark:hover:bg-gray-900/60",
                    isActive
                      ? "ring-2 ring-[#0EA5E9]/50 border-[#0EA5E9]/40 shadow-lg scale-[1.02]"
                      : "border-slate-200 dark:border-[#0EA5E9]/10",
                  ].join(" ")}
                  style={{ opacity: isActive ? 1 : 0.8 }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-lg">{getCountryFlag(country)}</span>
                    <span className="text-slate-700 dark:text-gray-200 text-xs truncate">
                      {country}
                    </span>
                    {isActive && (
                      <span className="text-[#0EA5E9] text-[10px] px-1.5 py-0.5 bg-[#0EA5E9]/20 rounded shrink-0">
                        ✓
                      </span>
                    )}
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 text-sm font-light ml-2">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatisticsPanel;
